import { Children, Fragment, FC, useEffect, useRef } from 'react';
import { Animated, Easing, Platform, StyleSheet, ViewStyle } from 'react-native'
import LinearGradient from 'react-native-linear-gradient'
import { withTheme } from 'styled-components/native'
import { Theme } from '../../theme/UiThemeProvider'
import { Box } from '../Box'

interface SkeletonPlaceHolderPropsInternal extends SkeletonPlaceHolderProps {
  theme?: Theme
}

interface SkeletonPlaceHolderProps {
  /**
   * Determines component's children.
   */
  children: JSX.Element | JSX.Element[]
  /**
   * Determines the color of placeholder. By default is #E1E9EE
   */
  backgroundColor?: string
  /**
   * Determines the highlight color of placeholder. By default is #F2F8FC
   */
  highlightColor?: string
  /**
   * Determines the animation speed in milliseconds. By default is 800
   */
  speed?: number
  hide?: boolean
}

const SkeletonPlaceHolderInternal: FC<SkeletonPlaceHolderPropsInternal> = ({
  children,
  backgroundColor = '#E1E9EE',
  speed = 800,
  highlightColor = '#F2F8FC',
  hide,
  theme,
}) => {
  const animatedValue = useRef(new Animated.Value(0))
  useEffect(() => {
    animatedValue.current.stopAnimation()
    if (!hide) {
      Animated.loop(
        Animated.timing(animatedValue.current, {
          toValue: 1,
          duration: speed,
          easing: Easing.ease,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ).start()
    }
  }, [animatedValue, speed, hide])

  if (hide) {
    return null
  }

  const getChildren = (element: JSX.Element | JSX.Element[]) => {
    return Children.map(element, (child: JSX.Element, index: number) => {
      const { children: skeletonChildren, ...style } = child.props
      if (skeletonChildren) {
        return (
          <Box key={index} {...style}>
            {getChildren(skeletonChildren)}
          </Box>
        )
      } else {
        const width = typeof style?.width === 'number' ? style?.width : theme?.sizes[style?.width] ?? 0
        const translateX = animatedValue.current.interpolate({
          inputRange: [0, 1],
          outputRange: [width * -1.5, width * 1.5],
        })
        return (
          <Box key={index}>
            <Box {...style} {...{ backgroundColor, overflow: 'hidden' }}>
              <Animated.View
                style={[
                  StyleSheet.absoluteFill,
                  {
                    transform: [{ translateX }],
                  },
                ]}>
                <LinearGradient
                  colors={[backgroundColor, highlightColor, backgroundColor] as string[]}
                  useAngle={true}
                  locations={[0.3, 0.5, 0.7]}
                  angle={100}
                  style={styles.linearGradient}
                />
              </Animated.View>
            </Box>
          </Box>
        )
      }
    });
  }

  return <Fragment>{getChildren(children)}</Fragment>;
}

const styles = {
  linearGradient: { flex: 1 } as ViewStyle,
}

export const SkeletonPlaceHolder: FC<SkeletonPlaceHolderProps> = withTheme(SkeletonPlaceHolderInternal)
